import { FC, useLayoutEffect } from 'react'
import { on, ROUTE_NAMES } from './config'
import { useRoute } from '$app/contexts/RouteContext/hooks'
import Login from '$pages/Login'
import PageLayout from '$layouts/PageLayout'
import GlobalAppUser from '$pages/GlobalServiceUser/List'
import GlobalDashboardUser from '$pages/GlobalDashboardUser/List'
import GlobalDashboardUserCreate from '$pages/GlobalDashboardUser/Create'
import UpdatePassword from '$pages/UpdatePassword'
import GlobalServiceUserAddApplication from '$pages/GlobalServiceUser/AddApplication'
import GlobalDashboardAddApplication from '$pages/GlobalDashboardUser/AddApplication'
import GlobalServiceUserIndexPage from '$pages/GlobalServiceUser/IndexPage'
import GlobalDashboardUserIndexPage from '$pages/GlobalDashboardUser/IndexPage'
import ApplicationUserRoutes from './ApplicationUserRoutes'
import DashboardUserRoutes from './DashboardUserRoutes'
import ApplicationRoutes from './ApplicationRoutes'
import GlobalSettingRoutes from './GlobalSettingRoutes'
import NotFound from '$pages/NotFound'
import IndexPage from '$pages/Apps/IndexPage'
import TenantGuard from '$blocks/TenantGuard'
import { updateAxiosHeaderTenant } from '$services/api'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import TenantOverview from '$pages/TenantOverview'
import AppUserAssignRoles from '$pages/AssignRoles'
import CreateApplication from '$pages/Apps/CreateApplication'
import AppUserCSVImport from '$pages/Apps/AppUser/AppUserCSVImport'
import AppRoleCSVImport from '$pages/Apps/AppRole/AppRoleCSVImport'
import AppPermissionCSVImport from '$pages/Apps/AppPermission/AppPermissionCSVImport'
import AppRoleCreate from '$pages/Apps/AppRole/Create'
import AppRoleEdit from '$pages/Apps/AppRole/Edit'
import AppPermissionCreate from '$pages/Apps/AppPermission/Create'
import AppPermissionEdit from '$pages/Apps/AppPermission/Edit'
import CountryCreate from '$pages/Country/Create'
import CountryEdit from '$pages/Country/Edit'

const FULL_PAGES: ROUTE_NAMES[] = [
  ROUTE_NAMES.CREATE_APP,

  ROUTE_NAMES.APP_ROLE_CREATE,
  ROUTE_NAMES.APP_ROLE_EDIT,
  ROUTE_NAMES.APP_ROLES_CSV_IMPORT,

  ROUTE_NAMES.APP_USERS_CSV_IMPORT,

  ROUTE_NAMES.APP_PERMISSION_CREATE,
  ROUTE_NAMES.APP_PERMISSION_EDIT,
  ROUTE_NAMES.APP_PERMISSIONS_CSV_IMPORT,

  ROUTE_NAMES.USER_ASSIGN_ROLES,
  ROUTE_NAMES.GLOBAL_SERVICE_USER_ATTACH_APP,

  ROUTE_NAMES.GLOBAL_MEMBER_CREATE,
  ROUTE_NAMES.GLOBAL_MEMBER_ATTACH_APP,

  ROUTE_NAMES.COUNTRY_CREATE,
  ROUTE_NAMES.COUNTRY_EDIT
]

export const Router: FC = () => {
  const isMounted = useIsMounted()

  const {
    is404,
    params: { tenantId = '' },
    name: routeName
  } = useRoute()

  useLayoutEffect(() => {
    updateAxiosHeaderTenant(tenantId)
  }, [tenantId])

  if (!isMounted) return null

  if (is404) return <NotFound />

  if (on(ROUTE_NAMES.LOGIN)) {
    return <Login />
  }

  if (on(ROUTE_NAMES.INDEX)) {
    return <IndexPage />
  }

  const isFullPage = FULL_PAGES.includes(routeName as ROUTE_NAMES)

  return (
    <TenantGuard>
      {isFullPage && (
        <>
          {on(ROUTE_NAMES.USER_ASSIGN_ROLES) && <AppUserAssignRoles />}

          {on(ROUTE_NAMES.CREATE_APP) && <CreateApplication />}

          {on(ROUTE_NAMES.GLOBAL_SERVICE_USER_ATTACH_APP) && (
            <GlobalServiceUserAddApplication />
          )}

          {on(ROUTE_NAMES.APP_USERS_CSV_IMPORT) && <AppUserCSVImport />}

          {on(ROUTE_NAMES.APP_ROLES_CSV_IMPORT) && <AppRoleCSVImport />}
          {on(ROUTE_NAMES.APP_ROLE_CREATE) && <AppRoleCreate />}
          {on(ROUTE_NAMES.APP_ROLE_EDIT) && <AppRoleEdit />}

          {on(ROUTE_NAMES.APP_PERMISSION_CREATE) && <AppPermissionCreate />}
          {on(ROUTE_NAMES.APP_PERMISSION_EDIT) && <AppPermissionEdit />}
          {on(ROUTE_NAMES.APP_PERMISSIONS_CSV_IMPORT) && (
            <AppPermissionCSVImport />
          )}

          {on(ROUTE_NAMES.GLOBAL_MEMBER_CREATE) && (
            <GlobalDashboardUserCreate />
          )}

          {on(ROUTE_NAMES.GLOBAL_MEMBER_ATTACH_APP) && (
            <GlobalDashboardAddApplication />
          )}

          {on(ROUTE_NAMES.COUNTRY_CREATE) && <CountryCreate />}
          {on(ROUTE_NAMES.COUNTRY_EDIT) && <CountryEdit />}
        </>
      )}

      {!isFullPage && (
        <PageLayout>
          {on(ROUTE_NAMES.TENANT_STATISTICS) && <TenantOverview />}

          <ApplicationRoutes />

          {on(ROUTE_NAMES.UPDATE_PASSWORD) && <UpdatePassword />}

          {on(ROUTE_NAMES.GLOBAL_APP_USERS) && <GlobalAppUser />}

          {on(ROUTE_NAMES.GLOBAL_MEMBERS) && <GlobalDashboardUser />}

          {/* SERVICE USERS ROUTES  */}
          {on(ROUTE_NAMES.GLOBAL_SERVICE_USER_INDEX) && (
            <GlobalServiceUserIndexPage />
          )}

          <ApplicationUserRoutes />

          {/* DASHBOARD USERS ROUTES  */}
          {on(ROUTE_NAMES.GLOBAL_MEMBER_INDEX) && (
            <GlobalDashboardUserIndexPage />
          )}

          <DashboardUserRoutes />

          <GlobalSettingRoutes />
          {/* MORE OTHER ROUTES  */}
        </PageLayout>
      )}
    </TenantGuard>
  )
}
